import { Link, useNavigate } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { formatOfferPrice, STUDY_OFFERS } from "@/lib/offers";
import { trackCheckoutStart, trackSignupIntent } from "@/lib/analytics";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Calendar, Settings } from "lucide-react";
import { SparklesIcon, ZapIcon, CrownIcon } from "@/components/icons/CustomIcons";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { useState } from "react";
import { toast } from "sonner";

const featureKeys = [
  { key: "sheetsPerDay", free: "2", pro: "∞" },
  { key: "aiTools", free: "5/day", pro: true },
  { key: "mcq", free: true, pro: true },
  { key: "comprehension", free: true, pro: true },
  { key: "pdfUpload", free: true, pro: true },
  { key: "youtube", free: true, pro: true },
  { key: "adaptiveLevel", free: false, pro: true },
  { key: "diagnostic", free: false, pro: true },
  { key: "gapAnalysis", free: false, pro: true },
  { key: "theDefense", free: false, pro: true },
  { key: "exportPdf", free: false, pro: true },
  { key: "spacedRepetition", free: true, pro: true },
  { key: "shareSheets", free: true, pro: true },
];

type PlanKey = "exam_week" | "monthly" | "annual";

const plans: { key: PlanKey; icon: React.ElementType; period: string; highlight?: boolean; badge?: string }[] = [
  { key: "exam_week", icon: ZapIcon, period: "pricing.examWeekPeriod", badge: "pricing.examWeekBadge" },
  { key: "monthly", icon: CrownIcon, period: "pricing.monthlyPeriod", highlight: true, badge: "pricing.popular" },
  { key: "annual", icon: Calendar, period: "pricing.annualPeriod", badge: "pricing.annualBadge" },
];

export default function PricingPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { subscribed, plan: currentPlan, subscriptionEnd, checkout, manageSubscription, loading: subLoading } = useSubscription();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  const handleCheckout = async (plan: PlanKey) => {
    if (!user) {
      trackSignupIntent("pricing_page");
      navigate("/sign-in");
      return;
    }
    trackCheckoutStart(plan);
    setCheckoutLoading(plan);
    try {
      await checkout(plan);
    } catch (err) {
      toast.error(t("pricing.checkoutError", "Unable to start checkout. Please try again."));
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManage = async () => {
    try {
      await manageSubscription();
    } catch {
      toast.error(t("pricing.portalError", "Unable to open subscription management."));
    }
  };

  return (
    <>
      <div className="container mx-auto px-4 sm:px-6 py-10 sm:py-16 max-w-5xl">
        {/* Header */}
        <section className="text-center mb-16">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">{t("pricing.badge", "Pricing")}</Badge>
          <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "var(--font-serif)" }}>
            System Atlas: {t("pricing.badge", "Pricing")}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("pricing.subtitle", "Start free and upgrade when you need more. System Atlas transforms your materials into effective study tools.")}
          </p>
        </section>

        {/* Active subscription banner */}
        {subscribed && currentPlan && (
          <section className="mb-10 p-4 rounded-xl border-2 border-primary bg-primary/5 flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="font-semibold text-primary">
                {t("pricing.activePlan", "Your current plan")}: <span>{t(`pricing.${currentPlan}`)}</span>
              </p>
              {subscriptionEnd && (
                <p className="text-sm text-muted-foreground">
                  {t("pricing.activeUntil", "Active until")} {new Date(subscriptionEnd).toLocaleDateString()}
                </p>
              )}
            </div>
            <Button variant="outline" onClick={handleManage} className="gap-2">
              <Settings className="w-4 h-4" /> {t("pricing.manageSubscription", "Manage")}
            </Button>
          </section>
        )}

        {/* Plans grid */}
        <section className="grid md:grid-cols-3 gap-6 mb-20 max-w-4xl mx-auto">

          {/* Paid plans */}
          {plans.map(p => {
            const Icon = p.icon;
            const isCurrentPlan = subscribed && currentPlan === p.key;
            return (
              <div key={p.key} className={`rounded-lg p-6 relative ${p.highlight ? "border-2 border-primary" : "border border-border/60"}`}>
                {p.badge && (
                  <Badge className={`absolute -top-3 left-4 ${p.highlight ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                    {t(p.badge, p.key)}
                  </Badge>
                )}
                {isCurrentPlan && (
                  <Badge className="absolute -top-3 right-4 bg-primary text-primary-foreground">
                    {t("pricing.yourPlan", "Your Plan")}
                  </Badge>
                )}
                <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
                  <Icon className="w-4 h-4 text-primary" />
                  {t(`pricing.${p.key}`, p.key)}
                </h2>
                <p className="text-muted-foreground text-sm mb-4">{t(`pricing.${p.key}Description`, "")}</p>
                <div className="mb-2">
                  <span className="text-3xl font-bold">{formatOfferPrice(p.key, i18n.language)}</span>
                  <span className="text-muted-foreground text-sm ml-1">{t(p.period, "")}</span>
                </div>
                {STUDY_OFFERS[p.key].trialDays > 0 && (
                  <p className="text-xs text-primary font-medium mb-4">
                    {t("pricing.freeTrial", "7 days free trial")}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mb-4 leading-relaxed min-h-16">
                  {t(`offerDetails.${p.key === "exam_week" ? "examTerms" : p.key === "monthly" ? "monthlyTerms" : "annualTerms"}`, { price: formatOfferPrice(p.key, i18n.language) })}
                  {STUDY_OFFERS[p.key].trialDays > 0 && <> {t("offerDetails.card")}</>}
                </p>

                {!user ? (
                  <Link to="/sign-in">
                     <Button className="w-full mb-6 gap-2 h-auto min-h-9 py-2 whitespace-normal" size="sm" variant={p.highlight ? "default" : "outline"}>
                       <SparklesIcon className="w-4 h-4 shrink-0" /> {t("offerDetails.accountCta")}
                     </Button>
                  </Link>
                ) : isCurrentPlan ? (
                   <Button className="w-full mb-6 gap-2 h-auto min-h-9 py-2 whitespace-normal" size="sm" variant="outline" onClick={handleManage}>
                     <Settings className="w-4 h-4 shrink-0" /> {t("pricing.manageSubscription", "Manage")}
                   </Button>
                ) : (
                   <Button
                     className="w-full mb-6 gap-2 h-auto min-h-9 py-2 whitespace-normal"
                     size="sm"
                     variant={p.highlight ? "default" : "outline"}
                     onClick={() => handleCheckout(p.key)}
                     disabled={checkoutLoading !== null || subLoading}
                   >
                     <SparklesIcon className="w-4 h-4 shrink-0" />
                     {checkoutLoading === p.key ? t("pricing.redirecting", "Redirecting…") : t("pricing.subscribe", "Subscribe")}
                   </Button>
                )}

                <ul className="space-y-2">
                  {featureKeys.map(f => (
                    <li key={f.key} className="flex items-center gap-2 text-sm">
                      <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                      <span>{t(`pricing.features.${f.key}`)}{typeof f.pro === "string" ? ` (${f.pro})` : ""}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </section>

        <section className="mb-12 border-y border-border py-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
            <div className="space-y-3">
              <h2 className="text-xl font-semibold">{t("offerDetails.freeTitle")}</h2>
              <ul className="space-y-2 text-sm">
                {[t("offerDetails.freeSheets"), t("offerDetails.freeActions"), t("offerDetails.freeNote")].map(line => (
                  <li key={line} className="flex items-start gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /><span>{line}</span></li>
                ))}
              </ul>
            </div>
            <Link to={user ? "/my-sheets" : "/sign-in"}><Button variant="outline">{t("offerDetails.accountCta")}</Button></Link>
          </div>
          <p className="mt-6 text-sm text-muted-foreground">{t("offerDetails.cancel")} {t("offerDetails.retention")}</p>
          <p className="mt-2 text-sm text-muted-foreground">{t("offerDetails.aiNotice")}</p>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-primary">
            <Link to="/terms">{t("refundPage.termsLink")}</Link>
            <Link to="/refund">{t("refundPage.title")}</Link>
            <Link to="/privacy">{t("refundPage.privacyLink")}</Link>
          </div>
        </section>

        {/* CTA */}
        {!user && (
          <section className="text-center p-10 bg-primary/5 border border-primary/15 rounded-2xl">
            <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: "var(--font-serif)" }}>{t("pricing.ctaTitle", "Start learning smarter today")}</h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">{t("pricing.ctaSubtitle", "Upload your first course and get an AI-generated revision sheet in seconds. No credit card required.")}</p>
            <Link to="/sign-in"><Button size="lg" className="gap-2"><SparklesIcon className="w-4 h-4" />{t("pricing.ctaButton", "Get Started Free")}</Button></Link>
          </section>
        )}
      </div>
    </>
  );
}
